// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import { types as sdkTypes, transit } from './sdkLoader';
import config from '../config';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const post = (path, body) => {
  if (typeof window === 'undefined') {
    return Promise.reject();
  }

  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

const get = path => {
  if (typeof window === 'undefined') {
    return Promise.reject();
  }

  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the booking data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const approveListings = listingId => {
  return post(`/api/listings/${listingId}/approve`, {});
};

export const addToCoachListOfEngagement = ({ addCoachList, removeCoachList, coachListingId }) => {
  return post(`/api/engagements/coaches/${coachListingId}/add`, {
    addCoachList,
    removeCoachList,
  });
};

export const removeCoachFromCoachList = ({ coachId, engagementId }) => {
  return post(`/api/engagements/${engagementId}/coaches/${coachId}/remove`, {});
};

export const getEmailForInvitation = ({ listingId, engagementId }) => {
  return get(`/api/engagements/${engagementId}/participants?listingId=${listingId}`);
};

export const createCoacheeUser = body => {
  return post(`/api/auth/create-coach-user`, body);
};

export const createUser = body => {
  return post(`/api/auth/create-user`, body);
};

export const inviteCoacheeToEngagement = engagementId => {
  return post(`/api/engagements/${engagementId}/invite-coachee`, {});
};

export const reviewCoach = ({ engagementId, ...rest }) => {
  return post(`/api/engagements/${engagementId}/review`, { ...rest });
};

export const pickCoach = ({ engagementId, coachListingId }) =>
  post(`/api/engagements/${engagementId}/select/${coachListingId}`, {});

export const scheduleChemistry = (engagementId, body) => {
  return post(`/api/engagements/${engagementId}/schedule-chemistry`, body);
};

export const markClaimAccount = () => post('/api/user/mark-claim-account', {});

export const getReportData = () => get('/api/report');

export const getNewAgreementReportData = () => get('/api/new-agreement-report');

// Transactions
// Initiate a transaction with a privileged transition.
//
// It will use to initiate transaction between buyer and admin, as well as 
// transition transaction to record transaction between buyer and coach
//
// See `server/api/transactions/transition-privileged` to see what data should
// be sent in the body.
export const initiatePrivilegedWithAdmin = body => {
  return post('/api/transactions/initiate-privileged-with-admin', body);
};

export const handleTransitNegotiatedTransition = body => {
  return post('/api/transactions/transit-negotiated-transition', body);
};

export const transitCoachRespond = body => {
  return post('/api/transactions/respond-buyer-invitation', body);
}

export const getEngagement = engagementId => get(`/api/engagements/${engagementId}`);

export const sendMedooLoginLink = () => post('/api/user/login-link', {});